import { computed } from 'vue'

export interface CurrentUser {
  id: string
  email: string
  firstName: string
  lastName: string
}

/**
 * Composable for getting current authenticated user information
 *
 * @returns Object with methods to get current user data
 *
 * @example
 * ```typescript
 * const { getUsername, getCurrentUser, currentUser } = useCurrentUser()
 *
 * const username = getUsername() // Returns email or 'anonymous'
 * const user = getCurrentUser() // Returns full user object or null
 * ```
 */
export function useCurrentUser() {
  /**
   * Get the current user object from localStorage
   * @returns CurrentUser object or null if not authenticated
   */
  const getCurrentUser = (): CurrentUser | null => {
    const userStr = localStorage.getItem('user')
    if (!userStr) {
      return null
    }

    try {
      const user = JSON.parse(userStr)
      return {
        id: user.id || '',
        email: user.email || '',
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      }
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error)
      return null
    }
  }

  /**
   * Get the current user's identifier (email)
   * This is used for createdBy fields in the backend
   *
   * @returns User email or 'anonymous' if not authenticated
   */
  const getUsername = (): string => {
    const user = getCurrentUser()
    return user?.email || 'anonymous'
  }

  /**
   * Get the current user's UUID
   * @returns User UUID or 'anonymous' if not authenticated
   */
  const getUserId = (): string => {
    const user = getCurrentUser()
    return user?.id || 'anonymous'
  }

  /**
   * Get the current user's full name for display
   * @returns Full name or email as fallback
   */
  const getDisplayName = (): string => {
    const user = getCurrentUser()
    if (!user) return 'Anonymous'
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
    return fullName || user.email || 'Anonymous'
  }

  /**
   * Check if user is authenticated
   * @returns true if user is authenticated
   */
  const isAuthenticated = (): boolean => {
    return getCurrentUser() !== null
  }

  // Reactive computed properties
  const currentUser = computed(() => getCurrentUser())
  const username = computed(() => getUsername())
  const displayName = computed(() => getDisplayName())
  const authenticated = computed(() => isAuthenticated())

  return {
    getCurrentUser,
    getUsername,
    getUserId,
    getDisplayName,
    isAuthenticated,
    currentUser,
    username,
    displayName,
    authenticated
  }
}
